<template>
  <div class="delete-profile-modal">
    <div class="delete-profile-modal__container">
      <!-- Иконка предупреждения -->
      <div class="delete-profile-modal__icon">
        <UIcon name="i-heroicons-exclamation-triangle" class="w-16 h-16 text-red-600 dark:text-red-500" />
      </div>

      <!-- Заголовок -->
      <h2 class="delete-profile-modal__title">Удаление профиля</h2>

      <!-- Предупреждение.
           Текст описывает то, что происходит на самом деле. Раньше здесь было обещание
           «безвозвратно удалить» личные данные, историю заказов, адреса и баллы —
           а сервер делает мягкое удаление и не стирает ничего из перечисленного.
           Обещание того, чего не происходит, — это самостоятельное нарушение
           (введение потребителя в заблуждение), отдельно от вопроса о самих данных. -->
      <div class="delete-profile-modal__warning">
        <p class="text-center mb-4">
          Вы уверены, что хотите
          <strong class="text-red-600 dark:text-red-500">закрыть</strong>
          свой профиль?
        </p>

        <div class="warning-box">
          <p class="warning-box__title">
            <UIcon name="i-heroicons-exclamation-circle" class="w-5 h-5" />
            <strong>Что произойдёт</strong>
          </p>
          <ul class="warning-box__list">
            <li>Вход в аккаунт станет невозможен, все сессии завершатся</li>
            <li>Накопленные баллы сгорят</li>
            <li>Имя и фамилия будут стёрты через 30 дней</li>
          </ul>
          <p class="warning-box__subtitle">Сохранится:</p>
          <ul class="warning-box__list">
            <li>История заказов и платежей — этого требует бухгалтерская отчётность</li>
            <li>Телефон и адреса — по ним работает защита от повторных бонусов</li>
          </ul>
          <p class="warning-box__note">
            Чтобы данные удалили полностью, напишите нам — контакты в
            <a href="/privacy" target="_blank">политике конфиденциальности</a>.
          </p>
        </div>
      </div>

      <!-- Поле подтверждения -->
      <div class="delete-profile-modal__confirmation">
        <label class="confirmation-label">
          Для подтверждения введите:
          <strong class="text-red-600 dark:text-red-400">УДАЛИТЬ</strong>
        </label>
        <UInput
          v-model="confirmationText"
          placeholder="Введите УДАЛИТЬ"
          size="xl"
          :disabled="isDeleting"
          class="confirmation-input"
        />
      </div>

      <!-- Кнопки -->
      <div class="delete-profile-modal__actions">
        <UiButtonMain
          label="Отмена"
          color="gray"
          size="xl"
          class="flex-1"
          :disabled="isDeleting"
          @click="handleCancel"
        />
        <UiButtonMain
          label="Удалить профиль"
          color="red"
          :accent="false"
          size="xl"
          class="flex-1"
          :loading="isDeleting"
          :disabled="confirmationText !== 'УДАЛИТЬ' || isDeleting"
          @click="handleDelete"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { consola } from 'consola';
import { useModalStore } from '@shared/model';
import apiProfileInstance from '~/entities/user';

const logger = consola.withTag('ModalDeleteProfile');

const modalStore = useModalStore();

const confirmationText = ref('');
const isDeleting = ref(false);

/**
 * Обрабатывает отмену удаления
 */
const handleCancel = () => {
  modalStore.close();
};

/**
 * Обрабатывает удаление профиля
 */
const handleDelete = async () => {
  if (confirmationText.value !== 'УДАЛИТЬ') {
    alert('Введите "УДАЛИТЬ" для подтверждения');
    return;
  }

  try {
    isDeleting.value = true;
    logger.log('[ModalDeleteProfile] 🗑️ Начинаем удаление профиля...');

    await apiProfileInstance.deleteUserProfile();

    logger.log('[ModalDeleteProfile] ✅ Профиль успешно удален');

    // Закрываем модалку
    modalStore.close();

    // Используем централизованный сервис разлогинивания
    const { AuthLogoutService, LogoutReason, UserType } = await import('~/entities/auth');
    await AuthLogoutService.logout({
      reason: LogoutReason.PROFILE_DELETED,
      userType: UserType.REGULAR,
      metadata: {
        source: 'ModalDeleteProfile',
        timestamp: Date.now(),
      },
    });
    isDeleting.value = false;
  } catch (error: any) {
    logger.error('[ModalDeleteProfile] ❌ Ошибка удаления профиля:', error);
    alert('Произошла ошибка при удалении профиля. Попробуйте еще раз.');
    isDeleting.value = false;
  }
};

defineExpose({ handleDelete });
</script>

<style scoped lang="scss">
.delete-profile-modal {
  @apply p-2.5 bg-white dark:bg-gray-900 max-w-2xl mx-auto;

  &__container {
    @apply flex flex-col gap-6 bg-white dark:bg-zinc-900 rounded-2xl p-6 md:p-8 max-md:h-[calc(100vh-20px)] justify-center;
  }

  &__icon {
    @apply flex justify-center;
  }

  &__title {
    @apply text-2xl md:text-3xl font-bold text-center text-gray-900 dark:text-white;
  }

  &__warning {
    @apply text-gray-700 dark:text-gray-300;
  }

  &__confirmation {
    @apply space-y-2;
  }

  &__actions {
    @apply flex gap-3 flex-col sm:flex-row;
  }
}

.warning-box {
  @apply bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4;

  &__title {
    @apply flex items-center gap-2 text-sm font-semibold text-red-800 dark:text-red-200 mb-2;
  }

  &__subtitle {
    @apply text-sm text-red-800 dark:text-red-200 mb-2;
  }

  &__list {
    @apply text-sm text-red-700 dark:text-red-300 list-disc list-inside space-y-1;

    & + .warning-box__subtitle {
      @apply mt-3;
    }
  }

  &__note {
    @apply text-xs text-red-700/80 dark:text-red-300/80 mt-3;

    a {
      @apply underline;
    }
  }
}

.confirmation-label {
  @apply block text-base font-medium text-gray-700 dark:text-gray-300;
}

.confirmation-input {
  @apply w-full;
}
</style>
